import api from '../api/ridershipApi';
import type { RequestParameters } from '../types/apiTypes';
import type { DirectionVariant } from '../types/gtfsTypes';
import type { RidershipFiltersCatalogResponse } from '../types/ridershipDirectionHistoryTypes';
import { RidershipDataReadinessResponse } from '../types/ridershipReadinessTypes';
import { AverageWeekdayBoardingSummaryRecord,
    AverageWeekdayBoardingTimelineRecord,
    AverageWeekdayStopSeatCapacityRecord,
    AverageWeekdayVehicleCapacityRecord,
    RidershipFilterCatalog,
    RidershipFilterCatalogDirection,
    RidershipFilterCatalogRoute,
    RidershipFilterCatalogTrip,
    RidershipHistoryRecord,
    RidershipHistoryResponse,
    RidershipStopActivityOverviewByStops,
    RidershipStopAggRecord,
    RidershipStopAggResponse,
    RidershipStopDirectionSummaryRecord,
    RidershipStopSummaryResponse } from '../types/ridershipTypes';
import { DayTypeFilter, normalizeDayTypeFilter } from '../utils/dayType';
import { normalizeRidershipDateOnly, normalizeTimelineMonthDate } from '../utils/ridershipDate';

function normalizeRouteIds(routeIds: string[]) {
  return Array.from(new Set((routeIds || [])
    .map(routeId => (routeId || '').trim())
    .filter(routeId => routeId.length > 0)))
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));
}

const rankingRoutesBatchSize = 25;

function chunkRoutes(routeIds: string[], batchSize: number): string[][] {
  if (routeIds.length === 0) {
    return [];
  }

  const batches: string[][] = [];
  for (let index = 0; index < routeIds.length; index += batchSize) {
    batches.push(routeIds.slice(index, index + batchSize));
  }

  return batches;
}

interface RidershipApiRow {
  serviceDay?: string;
  routeId?: string;
  routeShortName?: string;
  directionVariantId?: string;
  stopId?: string;
  stopCode?: string;
  stopExternalId?: string;
  stopName?: string;
  stopSequence?: number;
  longitude?: Longitude | null;
  latitude?: Latitude | null;
  actualArrivalTime?: string;
  actualDepartureTime?: string;
  scheduledArrivalTime?: string;
  boardingSum: number;
  boardingAvg: number;
  boardingAvgByTrip: number;
  alightingSum: number;
  alightingAvg: number;
  alightingAvgByTrip: number;
  loadSum: number;
  loadAvg: number;
  loadAvgByTrip: number;
  adjustedBoardingSum?: number | null;
  adjustedBoardingAvg?: number | null;
  adjustedBoardingAvgByTrip?: number | null;
  adjustedAlightingSum?: number | null;
  adjustedAlightingAvg?: number | null;
  adjustedAlightingAvgByTrip?: number | null;
  adjustedLoadSum?: number | null;
  adjustedLoadAvg?: number | null;
  adjustedLoadAvgByTrip?: number | null;
  tripCount: number;
  dayCount: number;
}

function normalizeRidershipRows(rows: RidershipApiRow[], aggregation: 'sum' | 'avg'): RidershipHistoryRecord[] {
  return rows.map((row: RidershipApiRow) => ({
    serviceDay: row.serviceDay,
    routeId: row.routeId ?? row.routeShortName ?? '',
    routeShortName: row.routeShortName ?? row.routeId ?? '',
    directionVariantId: row.directionVariantId,
    stopId: row.stopId,
    stopCode: row.stopCode,
    stopExternalId: row.stopExternalId,
    stopName: row.stopName,
    stopSequence: row.stopSequence,
    longitude: row.longitude ?? undefined,
    latitude: row.latitude ?? undefined,
    actualArrivalTime: row.actualArrivalTime,
    actualDepartureTime: row.actualDepartureTime,
    scheduledArrivalTime: row.scheduledArrivalTime,
    boarding: aggregation === 'avg' ? row.boardingAvg : row.boardingSum,
    load: aggregation === 'avg' ? row.loadAvg : row.loadSum,
    alighting: aggregation === 'avg' ? row.alightingAvg : row.alightingSum,
    boardingAvg: row.boardingAvg,
    boardingAvgByTrip: row.boardingAvgByTrip,
    boardingSum: row.boardingSum,
    alightingAvg: row.alightingAvg,
    alightingAvgByTrip: row.alightingAvgByTrip,
    alightingSum: row.alightingSum,
    loadAvg: row.loadAvg,
    loadAvgByTrip: row.loadAvgByTrip,
    loadSum: row.loadSum,
    adjustedBoardingAvg: row.adjustedBoardingAvg ?? undefined,
    adjustedBoardingAvgByTrip: row.adjustedBoardingAvgByTrip ?? undefined,
    adjustedBoardingSum: row.adjustedBoardingSum ?? undefined,
    adjustedAlightingAvg: row.adjustedAlightingAvg ?? undefined,
    adjustedAlightingAvgByTrip: row.adjustedAlightingAvgByTrip ?? undefined,
    adjustedAlightingSum: row.adjustedAlightingSum ?? undefined,
    adjustedLoadAvg: row.adjustedLoadAvg ?? undefined,
    adjustedLoadAvgByTrip: row.adjustedLoadAvgByTrip ?? undefined,
    adjustedLoadSum: row.adjustedLoadSum ?? undefined,
    tripCount: row.tripCount,
    dayCount: row.dayCount,
  }));
}

interface RidershipStopAggregateApiRow {
  stopId: string;
  stopName: string;
  stopCode?: string | null;
  stopExternalId?: string | null;
  stopSequence?: number | null;
  directionVariantId?: string;
  routeId?: string;
  dayCount: number;
  tripCount: number;
  boardingSum: number;
  boardingAvg: number;
  alightingSum: number;
  alightingAvg: number;
  loadSum: number;
  loadAvg: number;
}

function normalizeStopAggRows(rows: RidershipStopAggregateApiRow[]): RidershipStopAggRecord[] {
  return rows
    .map((row: RidershipStopAggregateApiRow) => ({
      stopId: row.stopId,
      stopCode: row.stopCode ?? undefined,
      stopExternalId: row.stopExternalId ?? undefined,
      stopName: row.stopName,
      stopSequence: row.stopSequence ?? undefined,
      directionVariantId: row.directionVariantId,
      routeId: row.routeId,
      dayCount: row.dayCount,
      boarding: row.boardingSum,
      load: row.loadSum,
      alighting: row.alightingSum,
      boardingAvg: row.boardingAvg,
      boardingSum: row.boardingSum,
      alightingAvg: row.alightingAvg,
      alightingSum: row.alightingSum,
      loadAvg: row.loadAvg,
      loadSum: row.loadSum,
      tripCount: row.tripCount,
    }))
    .filter(row => row.stopSequence === undefined || row.stopSequence === null || row.stopSequence >= 0);
}

interface RidershipStopSummaryApiRow {
  stopId: string;
  stopName: string;
  stopCode?: string;
  stopExternalId?: string;
  routeId: string;
  routeShortName?: string;
  directionVariantId?: string;
  boardingSum: number;
  boardingAvg: number;
  alightingSum: number;
  alightingAvg: number;
  loadSum: number;
  loadAvg: number;
  dayCount: number;
}

function normalizeStopSummaryRows(rows: RidershipStopSummaryApiRow[]): RidershipStopDirectionSummaryRecord[] {
  return rows.map((row: RidershipStopSummaryApiRow) => ({
    stopId: row.stopId,
    stopName: row.stopName,
    stopCode: row.stopCode,
    stopExternalId: row.stopExternalId,
    routeId: row.routeId,
    routeShortName: row.routeShortName ?? row.routeId,
    directionVariantId: row.directionVariantId,
    boarding: row.boardingSum,
    load: row.loadSum,
    alighting: row.alightingSum,
    boardingAvg: row.boardingAvg,
    boardingSum: row.boardingSum,
    alightingAvg: row.alightingAvg,
    alightingSum: row.alightingSum,
    loadAvg: row.loadAvg,
    loadSum: row.loadSum,
    dayCount: row.dayCount,
  }));
}


interface AverageWeekdayBoardingSummaryApiRow {
  routeShortName: string;
  fiscalYear: string;
  monthNumber: number;
  monthName: string;
  boarding: number;
  type?: string | null;
}

function normalizeAverageWeekdayBoardingSummaryRows(rows: AverageWeekdayBoardingSummaryApiRow[]): AverageWeekdayBoardingSummaryRecord[] {
  return rows.map((row: AverageWeekdayBoardingSummaryApiRow) => ({
    routeShortName: row.routeShortName,
    fiscalYear: row.fiscalYear,
    monthNumber: row.monthNumber,
    monthName: row.monthName,
    boarding: row.boarding,
    type: row.type?.trim() || undefined,
  }));
}

interface AverageWeekdayBoardingTimelineApiRow {
  routeShortName: string;
  fiscalYear?: string;
  monthName?: string;
  monthDate: string;
  boarding: number;
}

function normalizeAverageWeekdayBoardingTimelineRows(rows: AverageWeekdayBoardingTimelineApiRow[]): AverageWeekdayBoardingTimelineRecord[] {
  return rows.map((row: AverageWeekdayBoardingTimelineApiRow) => {
    const monthDate = normalizeTimelineMonthDate(row.monthDate);
    return {
      routeShortName: row.routeShortName,
      fiscalYear: String(row.fiscalYear ?? ''),
      monthName: String(row.monthName ?? ''),
      monthDate,
      boarding: row.boarding,
    };
  });
}

interface AverageWeekdayVehicleCapacityApiRow {
  vehicleId: string;
  vehicleTypeCode?: string | null;
  averageLoad: number;
  peakLoad: number;
  averageBoarding: number;
  averageAlighting: number;
  seats: number;
  standings: number;
  handicapped: number;
  totalCapacity?: number | null;
  utilizationPercent: number;
}

function normalizeAverageWeekdayVehicleCapacityRows(rows: AverageWeekdayVehicleCapacityApiRow[]): AverageWeekdayVehicleCapacityRecord[] {
  return rows
    .filter(row => row.vehicleId)
    .map((row: AverageWeekdayVehicleCapacityApiRow) => {
      const totalCapacity = row.totalCapacity ?? (row.seats + row.standings + row.handicapped);
      return {
        vehicleId: row.vehicleId,
        vehicleTypeCode: row.vehicleTypeCode ?? undefined,
        averageLoad: row.averageLoad,
        peakLoad: row.peakLoad,
        averageBoarding: row.averageBoarding,
        averageAlighting: row.averageAlighting,
        seats: row.seats,
        standings: row.standings,
        handicapped: row.handicapped,
        totalCapacity,
        utilizationPercent: row.utilizationPercent,
      };
    });
}
interface AverageWeekdayStopSeatCapacityApiRow {
  stopId: string;
  stopName: string;
  stopSequence?: number | null;
  seats: number;
  standings: number;
  handicapped: number;
}

function normalizeAverageWeekdayStopSeatCapacityRows(rows: AverageWeekdayStopSeatCapacityApiRow[]): AverageWeekdayStopSeatCapacityRecord[] {
  return rows.map((row: AverageWeekdayStopSeatCapacityApiRow) => {
    return {
      stopId: row.stopId,
      stopName: row.stopName,
      stopSequence: row.stopSequence ?? undefined,
      seats: row.seats + row.standings + row.handicapped,
      standings: row.standings,
      handicapped: row.handicapped,
    };
  }).filter(row => row.stopSequence === undefined || row.stopSequence === null || row.stopSequence >= 0);
}

function normalizeRidershipFilterCatalog(data: RidershipFiltersCatalogResponse): RidershipFilterCatalog {
  const routes = data.routes
    .map((route): RidershipFilterCatalogRoute => {
      const directions = route.directions
        .map((direction): RidershipFilterCatalogDirection => {
          const trips = (direction.trips ?? [])
            .map((trip): RidershipFilterCatalogTrip => ({
              tripId: trip.tripId,
              shortTripId: trip.shortTripId,
              friendlyName: trip.friendlyName,
              tripHeadsign: trip.tripHeadsign ?? undefined,
            }))
            .filter((trip: RidershipFilterCatalogTrip) => trip.tripId.length > 0)
            .sort((left: RidershipFilterCatalogTrip, right: RidershipFilterCatalogTrip) => left.tripId.localeCompare(right.tripId, undefined, { sensitivity: 'base' }));

          const rawDirectionVariantInternalIds: string[] = direction.directionVariantInternalIds
            .map(id => String(id ?? '').trim())
            .filter((id: string) => id.length > 0);
          const directionVariantInternalIds: string[] = Array.from(new Set<string>(rawDirectionVariantInternalIds))
            .sort((left: string, right: string) => left.localeCompare(right, undefined, { sensitivity: 'base' }));

          return {
            directionVariantId: direction.directionVariantId,
            directionLabel: direction.directionLabel,
            firstStopName: direction.firstStopName ?? '',
            directionVariantInternalIds,
            trips,
          };
        })
        .filter((direction: RidershipFilterCatalogDirection) => direction.directionVariantId.length > 0)
        .sort((left: RidershipFilterCatalogDirection, right: RidershipFilterCatalogDirection) =>
          (left.directionLabel || '').localeCompare((right.directionLabel || ''), undefined, { sensitivity: 'base' }));

      return {
        routeId: route.routeId,
        routeName: route.routeName || route.routeId,
        directions,
      };
    })
    .filter((route: RidershipFilterCatalogRoute) => route.routeId.length > 0)
    .sort((left: RidershipFilterCatalogRoute, right: RidershipFilterCatalogRoute) =>
      (left.routeName || '').localeCompare((right.routeName || ''), undefined, { sensitivity: 'base' }));

  return {
    fromDate: data.fromDate,
    toDate: data.toDate,
    routes,
  };
}

interface RidershipStopActivityOverviewApiResponse {
  stopId: string;
  stopName: string;
  summaries: RidershipStopSummaryApiRow[];
}

interface RidershipStopActivityOverviewByStopsApiResponse {
  summaries: RidershipStopSummaryApiRow[];
  directionVariants: DirectionVariant[];
  stopActivity: RidershipStopAggregateApiRow[];
}


// Route-based ridership history now returns stop aggregates
function toIsoDate(d: Date | string | null | undefined): string | undefined {
  return normalizeRidershipDateOnly(d);
}

export async function getRidershipHistory(
    agencyId: string,
    routeId: string,
    directions: string,
    fromDate: string,
    toDate: string,
    dayType: DayTypeFilter = 'workdays',
    actualArrivalFrom: string,
    actualArrivalTo: string,
    tripId?: string,
    directionIds?: string[],
    tripIds?: string[],
    parameters?: RequestParameters,
): Promise<RidershipStopAggResponse> {
  const result = await api.getRidershipHistoryByRoute(agencyId, routeId, directions, fromDate, toDate, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, tripId, directionIds, tripIds, parameters);
  return normalizeStopAggRows(result.data);
}

export async function getRidershipFilterCatalog(
  agencyId: string,
  fromDate?: string,
  toDate?: string,
  parameters?: RequestParameters,
): Promise<RidershipFilterCatalog> {
  const result = await api.getRidershipFilterCatalog(agencyId, fromDate, toDate, parameters);
  return normalizeRidershipFilterCatalog(result.data);
}

// Aggregated sums for Ranking page (legacy response shape used by Ranking UI)
export async function getRidershipSumByRoute(agencyId: string, fromDate?: Date | string, toDate?: Date | string, dayType: DayTypeFilter = 'workdays', parameters?: RequestParameters): Promise<RidershipHistoryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipSumByRoute(agencyId, fromIso, toIso, normalizeDayTypeFilter(dayType), parameters);
  return normalizeRidershipRows(result.data, 'sum');
}

export async function getRidershipAvgByRoute(agencyId: string, fromDate?: Date | string, toDate?: Date | string, dayType: DayTypeFilter = 'workdays', parameters?: RequestParameters): Promise<RidershipHistoryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipAvgByRoute(agencyId, fromIso, toIso, normalizeDayTypeFilter(dayType), parameters);
  return normalizeRidershipRows(result.data, 'avg');
}

export async function getRidershipSumByStop(agencyId: string, fromDate?: Date | string, toDate?: Date | string, dayType: DayTypeFilter = 'workdays', parameters?: RequestParameters): Promise<RidershipHistoryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipSumByStop(agencyId, fromIso, toIso, normalizeDayTypeFilter(dayType), parameters);
  return normalizeRidershipRows(result.data, 'sum');
}

export async function getRidershipAvgByStop(agencyId: string, fromDate?: Date | string, toDate?: Date | string, dayType: DayTypeFilter = 'workdays', parameters?: RequestParameters): Promise<RidershipHistoryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipAvgByStop(agencyId, fromIso, toIso, normalizeDayTypeFilter(dayType), parameters);
  return normalizeRidershipRows(result.data, 'avg');
}

export async function getRidershipStopSummary(
  agencyId: string,
  stopId: string,
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<RidershipStopSummaryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipStopSummary(agencyId, stopId, fromIso, toIso, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, parameters);
  return normalizeStopSummaryRows(result.data);
}

export async function getRidershipStopActivityOverview(
  agencyId: string,
  stopId: string,
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<{
  stopId: string;
  stopName: string;
  summaries: RidershipStopDirectionSummaryRecord[];
}> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipStopActivityOverview(agencyId, stopId, fromIso, toIso, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, parameters);
  const data: RidershipStopActivityOverviewApiResponse = result.data;
  return {
    stopId: data.stopId,
    stopName: data.stopName,
    summaries: normalizeStopSummaryRows(data.summaries),
  };
}

export async function getRidershipStopActivityOverviewByStops(
  agencyId: string,
  stopIds: string[],
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<RidershipStopActivityOverviewByStops> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipStopActivityOverviewByStops(agencyId, stopIds, fromIso, toIso, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, parameters);
  const data: RidershipStopActivityOverviewByStopsApiResponse = result.data;
  return {
    summaries: normalizeStopSummaryRows(data.summaries),
    directionVariants: data.directionVariants,
    stopActivity: normalizeStopAggRows(data.stopActivity),
  };
}

export async function getRidershipStopActivityByStops(
  agencyId: string,
  stopIds: string[],
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<RidershipStopAggRecord[]> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipStopActivityByStops(agencyId, stopIds, fromIso, toIso, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, parameters);
  return normalizeStopAggRows(result.data);
}

export async function getRidershipStopActivityByRoutes(
  agencyId: string,
  routeIds: string[],
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<RidershipStopAggRecord[]> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const result = await api.getRidershipStopActivityByRoutes(agencyId, routeIds, fromIso, toIso, normalizeDayTypeFilter(dayType), actualArrivalFrom, actualArrivalTo, parameters);
  return normalizeStopAggRows(result.data);
}

export async function getRidershipStopsRankingByRoutes(
  agencyId: string,
  routeIds: string[],
  fromDate?: Date | string,
  toDate?: Date | string,
  dayType: DayTypeFilter = 'workdays',
  aggregation: 'sum' | 'avg' = 'sum',
  parameters?: RequestParameters,
): Promise<RidershipHistoryResponse> {
  const fromIso = toIsoDate(fromDate);
  const toIso = toIsoDate(toDate);
  const normalizedRouteIds = normalizeRouteIds(routeIds);
  if (normalizedRouteIds.length === 0) {
    return [];
  }

  const routeBatches = chunkRoutes(normalizedRouteIds, rankingRoutesBatchSize);
  const requests = routeBatches.map(routeBatch => api.getRidershipStopsRankingByRoutes(agencyId, {
    routeIds: routeBatch,
    fromDate: fromIso,
    toDate: toIso,
    dayType: normalizeDayTypeFilter(dayType),
    aggregation,
  }, parameters));
  const responses = await Promise.all(requests);
  return responses.flatMap(response => normalizeRidershipRows(response.data, aggregation));
}


export async function getAverageWeekdayBoardingSummary(
  agencyId: string,
  routeIds: string[],
  dayType: DayTypeFilter = 'workdays',
  aggregation: 'sum' | 'avg' = 'sum',
  parameters?: RequestParameters,
): Promise<AverageWeekdayBoardingSummaryRecord[]> {
  const normalizedRouteIds = normalizeRouteIds(routeIds);
  const result = await api.getAverageWeekdayBoardingSummaryBatch(agencyId, {
    routeIds: normalizedRouteIds,
    dayType: normalizeDayTypeFilter(dayType),
    aggregation,
  }, parameters);
  return normalizeAverageWeekdayBoardingSummaryRows(result.data);
}

export async function getAverageWeekdayBoardingTimeline(
  agencyId: string,
  routeIds: string[],
  dayType: DayTypeFilter = 'workdays',
  aggregation: 'sum' | 'avg' = 'sum',
  parameters?: RequestParameters,
): Promise<AverageWeekdayBoardingTimelineRecord[]> {
  const normalizedRouteIds = normalizeRouteIds(routeIds);
  const result = await api.getAverageWeekdayBoardingTimelineBatch(agencyId, {
    routeIds: normalizedRouteIds,
    dayType: normalizeDayTypeFilter(dayType),
    aggregation,
  }, parameters);
  return normalizeAverageWeekdayBoardingTimelineRows(result.data);
}

export async function getAverageWeekdayVehicleCapacity(
  agencyId: string,
  routeId: string,
  direction: string,
  fromDate?: string,
  toDate?: string,
  dayType: DayTypeFilter = 'workdays',
  tripId?: string,
  directionIds?: string[],
  tripIds?: string[],
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<AverageWeekdayVehicleCapacityRecord[]> {
  const result = await api.getAverageWeekdayVehicleCapacity(
    agencyId,
    routeId,
    direction,
    fromDate,
    toDate,
    dayType,
    tripId,
    directionIds,
    tripIds,
    actualArrivalFrom,
    actualArrivalTo,
    parameters,
  );
  return normalizeAverageWeekdayVehicleCapacityRows(result.data);
}

export async function getAverageWeekdayStopSeatCapacity(
  agencyId: string,
  routeId: string,
  direction: string,
  fromDate?: string,
  toDate?: string,
  dayType: DayTypeFilter = 'workdays',
  tripId?: string,
  directionIds?: string[],
  tripIds?: string[],
  actualArrivalFrom?: string,
  actualArrivalTo?: string,
  parameters?: RequestParameters,
): Promise<AverageWeekdayStopSeatCapacityRecord[]> {
  const result = await api.getAverageWeekdayStopSeatCapacity(
    agencyId,
    routeId,
    direction,
    fromDate,
    toDate,
    dayType,
    tripId,
    directionIds,
    tripIds,
    actualArrivalFrom,
    actualArrivalTo,
    parameters,
  );
  return normalizeAverageWeekdayStopSeatCapacityRows(result.data);
}

// Utility endpoint: get route IDs that have exactly X unique active stops (default 2)
export async function getLightRailRouteIds(agencyId: string): Promise<string[]> {
  const result = await api.getLightRailRouteIds(agencyId);
  return result.data;
}

export async function getRidershipDataReadiness(
  agencyId: string,
  fiscalYear?: number,
  dailyFrom?: string,
  dailyTo?: string,
  parameters?: RequestParameters,
): Promise<RidershipDataReadinessResponse> {
  const result = await api.getRidershipDataReadiness(agencyId, fiscalYear, dailyFrom, dailyTo, parameters);
  return result.data;
}
